/**
 * Build-time version metadata (DCP-294).
 *
 * The CI/Docker build stamps `VITE_APP_*` variables into the bundle at build
 * time (via `import.meta.env`, Vite's standard mechanism). Version and build
 * date are per-artifact facts, so they stay baked. The channel, however, is
 * ALSO readable at runtime via `window.__ENV__.APP_CHANNEL` (see runtime-env.ts)
 * so a single image can be re-tagged across environments (dev image → :staging)
 * and still report the correct channel — the build-time value is only a fallback.
 *
 * Local `pnpm dev` runs without these variables set, so every getter falls
 * back to a sensible default ('dev' version / empty date / 'local' channel).
 */
import { getRuntimeChannel } from '@/lib/runtime-env'

/**
 * App version stamped at build time.
 * @returns the semantic version string, or 'dev' if not injected (local dev build)
 */
export function getAppVersion(): string {
  return import.meta.env.VITE_APP_VERSION ?? 'dev'
}

/**
 * Build timestamp stamped at build time.
 * @returns an ISO-8601 date string, or '' if not injected
 */
export function getBuildDate(): string {
  return import.meta.env.VITE_APP_BUILD_DATE ?? ''
}

/**
 * Release channel for this deployment (e.g. "dev", "stage").
 *
 * Prefers the runtime value (`window.__ENV__.APP_CHANNEL`) so a re-tagged image
 * reports the environment it actually runs in; falls back to the build-time bake,
 * then 'local' (local dev build).
 * @returns the channel name
 */
export function getChannel(): string {
  return getRuntimeChannel() ?? import.meta.env.VITE_APP_CHANNEL ?? 'local'
}

/**
 * Format an ISO build date into a short human-readable date (e.g. "2026-07-21").
 *
 * @param iso - ISO-8601 date string (may be empty or malformed)
 * @returns a `YYYY-MM-DD` date string, or '' if `iso` is empty or unparseable
 */
export function formatBuildDate(iso: string): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  // A build stamp is genuinely a UTC instant, not a business day: it records when
  // CI produced the artifact and must read identically for every viewer. This is
  // NOT a case for businessDate().
  // eslint-disable-next-line no-restricted-syntax -- build stamp, deliberately UTC
  return date.toISOString().slice(0, 10)
}

/**
 * Build the "vX.Y.Z · <date>" label shown in the user menu and the About dialog.
 * Falls back to just the version when no build date is available (e.g. local dev).
 *
 * @param version - version string from getAppVersion() ('dev' fallback included)
 * @param formattedDate - short human date from formatBuildDate(), or ''
 * @returns e.g. "v1.4.2 · 2026-07-21", "v1.4.2", or "dev"
 */
export function formatVersionLabel(version: string, formattedDate: string): string {
  // `git describe` already yields a leading "v" (e.g. "v0.1.0", "v0.1.0-3-gsha"),
  // while a bare semver ("1.4.2") does not. Strip any existing "v" first so we
  // never render a double "vv" prefix regardless of the injected form.
  const versionLabel = version === 'dev' ? 'dev' : `v${version.replace(/^v/, '')}`
  return formattedDate ? `${versionLabel} · ${formattedDate}` : versionLabel
}
